import { ColumnSizingState } from '@tanstack/angular-table'
import { readLocalStorage, writeLocalStorage } from '../local-storage'

/** TanStack's own floor is 20px, which is too narrow for a header label beside its filter and sort buttons. */
export const MIN_COLUMN_WIDTH = 80

/** TanStack's default ceiling is `Number.MAX_SAFE_INTEGER`, which makes a "widen to the maximum" key meaningless. */
export const MAX_COLUMN_WIDTH = 1200

/** Only reached for a column the rest of the table was measured without, so it has no share to read. */
export const DEFAULT_COLUMN_WIDTH = 180

function toStorageKey(tableId: string): string {
  return `app-table:${tableId}:column-widths`
}

export function clampWidth(width: number, minWidth: number, maxWidth: number): number {
  return Math.round(Math.min(Math.max(width, minWidth), maxWidth))
}

function isUsableWidth(width: unknown): width is number {
  return typeof width === 'number' && Number.isFinite(width) && width > 0
}

/**
 * Stored widths are whatever was in `localStorage` last time, so a renamed or removed column is
 * expected rather than exceptional: anything `knownColumnIds` doesn't recognise is dropped instead of
 * accumulating forever.
 */
export function readColumnWidths(tableId: string, knownColumnIds: Set<string>): ColumnSizingState {
  if (tableId.length === 0) {
    return {}
  }
  const stored = parseStoredWidths(readLocalStorage(toStorageKey(tableId)))
  return Object.entries(stored).reduce<ColumnSizingState>(
    (widths, [columnId, width]) => (knownColumnIds.has(columnId) && isUsableWidth(width) ? { ...widths, [columnId]: width } : widths),
    {}
  )
}

function parseStoredWidths(raw: string | null): Record<string, unknown> {
  if (raw === null) {
    return {}
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** The rest of the map is the browser's own measurement, which belongs to a window size, not a user. */
export function pickWidths(widths: ColumnSizingState, columnIds: Set<string>): ColumnSizingState {
  return Object.fromEntries(Object.entries(widths).filter(([columnId]) => columnIds.has(columnId)))
}

export function saveColumnWidths(tableId: string, widths: ColumnSizingState) {
  if (tableId.length === 0) {
    return
  }
  writeLocalStorage(toStorageKey(tableId), JSON.stringify(widths))
}

export function clearColumnWidths(tableId: string) {
  if (tableId.length === 0) {
    return
  }
  writeLocalStorage(toStorageKey(tableId), null)
}

/** Read from the element rather than restated as a constant, so the stylesheet stays the only owner. */
function horizontalPadding(element: HTMLElement): number {
  const { paddingLeft, paddingRight } = getComputedStyle(element)
  return parseFloat(paddingLeft) + parseFloat(paddingRight)
}

/**
 * The width a cell's text wants, which is not what the cell itself reports — the cell clips its text,
 * while a range over its contents measures the text unclipped and needs no wrapper element to do it.
 */
function measureTextWidth(cell: HTMLElement): number {
  const range = document.createRange()
  range.selectNodeContents(cell)
  return range.getBoundingClientRect().width
}

export function measureContentWidth(cells: HTMLElement[]): number {
  return cells.reduce((widest, cell) => Math.max(widest, Math.ceil(measureTextWidth(cell)) + horizontalPadding(cell)), 0)
}

/**
 * What a header needs to show its label in full: the label itself plus the filter and sort buttons,
 * which keep their size while the label is the part that ellipsises. The label is a shrunk flex item,
 * so its rect reports the width it was given and `scrollWidth` the width it wanted.
 */
export function measureHeaderWidth(header: HTMLElement | null): number {
  const content = header?.querySelector<HTMLElement>('.header-content') ?? null
  const label = header?.querySelector<HTMLElement>('.header-label') ?? null
  if (header === null || content === null || label === null) {
    return 0
  }
  const gap = parseFloat(getComputedStyle(content).columnGap) || 0
  const controls = Array.from(content.querySelectorAll<HTMLElement>('.icon-button'))
  const controlsWidth = controls.reduce((total, control) => total + control.offsetWidth + gap, 0)
  return Math.ceil(Math.max(label.scrollWidth, label.getBoundingClientRect().width)) + controlsWidth + horizontalPadding(header)
}
